<template>
  <div ref="fileUploaderContainer" class="file-uploader" :class="cssClasses">
    <!-- file uploader component -->
    <div v-if="!showFileEntry || fileResponseFailed || !fileEntry" class="acceptable-files-info">
      <!-- General label, File size limit label, and Dimensions label -->
      <div>
        {{ t("file_upload_label_info") }}
        <cv-link target="_blank" :href="acceptableFileUrl">
          {{ t("file_upload_label_info_here") }}
        </cv-link>
      </div>
    </div>

    <div class="uploader-wrapper">
      <cv-file-uploader
        v-if="!showPreview"
        ref="fileUploader"
        v-model="fileEntry"
        :accept="acceptedFiles"
        :clear-on-reselect="clearOnReselect"
        :removable="removable"
        :initial-state-uploading="initialStateUploading"
        @update:model-value="handleFileChange"
      >
        <template #drop-target>
          <message-component :has-icon="false">
            {{ t("file_upload_drag_drop_message") }}
          </message-component>
        </template>
      </cv-file-uploader>
    </div>

    <div v-if="showImagePreview" class="image-placeholder">
      <img :src="imgSrc || ''" class="thumbnail" alt="" />
    </div>

    <attachment-placeholder
      v-if="showFileEntry"
      :kind="error.title ? 'error' : 'info'"
      :media-title="fileName"
      :error="error"
      :icon-type="fileType ? iconType(fileType) : ''"
    >
      <template #action-tools>
        <button
          v-if="showCroppingTool"
          :disabled="isUploading"
          class="crop"
          @click="handleCropping"
        >
          <svg-icon name="Crop24" kind="action" />
        </button>
        <button :disabled="isUploading" class="trash" @click="handleRemove">
          <svg-icon name="Trash24" kind="action" />
        </button>
      </template>
    </attachment-placeholder>
  </div>
</template>

<script setup lang="ts">
import appConfigs from "@configs/app";
import { computed, onBeforeMount, PropType, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import CroppingCanvasModal from "@plugins/cropping/components/CroppingCanvasModal.vue";
import AttachmentPlaceholder from "@plugins/common/components/AttachmentPlaceholder.vue";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { notNull } from "@utils/var.check";
import { MEDIA_TYPE } from "@plugins/media/media.constant";
import {
  FileResponse,
  FileStatus,
  FileType,
  FileUploadEntry,
  UploadFlowValues,
} from "@plugins/file-uploader-v2/types.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import {
  useAcceptedFileExtensions,
  useFileUploader,
} from "@plugins/file-uploader-v2/composables/useFileUploader.ts";

const iconType = (fileType: string) => {
  switch (fileType) {
    case MEDIA_TYPE.VIDEO:
      return "Video32";
    case MEDIA_TYPE.AUDIO:
      return "Audio32";
    case MEDIA_TYPE.IMAGE:
      return "Image32";
    case MEDIA_TYPE.PDF:
      return "PDF32";
    case MEDIA_TYPE.POWERPOINT:
      return "Powerpoint32";
    case MEDIA_TYPE.EXCEL:
      return "Excel32";
    case MEDIA_TYPE.WORD:
      return "Word32";
    case MEDIA_TYPE.HTML5:
      return "Interactive32";
    case MEDIA_TYPE.ZIP:
    case MEDIA_TYPE.OTHERS:
      return "Folder32";
    default:
      return "";
  }
};

// Props
const props = defineProps({
  addedFile: { type: File, default: null },
  croppingConfiguration: { type: Object, default: null }, // Do not pass in cropping Configuration unless image is meant to be cropped.
  disabled: { type: Boolean, default: false },
  removeFile: { type: Boolean, default: false },
  preview: { type: Boolean, default: true },
  uploadFlow: { type: String as PropType<UploadFlowValues>, default: null },
  customAcceptableFileUrl: { type: String, default: null },
  dirtyId: { type: String, default: null },
});

const clearOnReselect = ref(true);
const initialStateUploading = ref(false);
const removable = ref(true);

// Data
const fileUploader = useTemplateRef<unknown>("fileUploader");
const imgSrc = ref<string | null>(null);

const error = ref<{ title: string; message: string | string[] }>({ title: "", message: "" });
const isUploading = ref(false);
const cropModalId = ref<string | null>(null);
const fileEntry = ref<FileUploadEntry | null>(null);
const fileResponse = ref<FileResponse | null>(null);

const emit = defineEmits(["update:fileEntry", "fileChanged"]);

const { t } = useI18n();
const modal = useModal();
const store = useStore();

// Computed
const isTeacher = computed(() => store.getters["auth/isTeacher"]);
const fileName = computed(() => fileEntry.value?.file?.name ?? "");

const fileValid = computed(() => fileEntry.value?.isValid);
const fileType = computed(() => (fileEntry.value ? getMediaType(fileEntry.value.file) : ""));
const fileResponseFailed = computed(() => fileResponse.value?.status === FileStatus.FAILED);
const uploadFlow = computed(() => props.uploadFlow);

const acceptedFiles = useAcceptedFileExtensions(uploadFlow);
const acceptableFileUrl = computed(() => {
  if (notNull(props.customAcceptableFileUrl)) {
    return props.customAcceptableFileUrl;
  } else if (isTeacher.value) {
    return appConfigs.acceptedFileGuideLocation;
  } else {
    return appConfigs.acceptedFileGuideStudentLocation;
  }
});

const showFileEntry = computed(() => fileValid.value || error.value.title !== "");
const showPreview = computed(
  () => props.preview && fileValid.value && !fileResponseFailed.value && !error.value.title,
);
const showImagePreview = computed(() => showPreview.value && fileType.value === FileType.IMAGE);
const showCroppingTool = computed(() => fileValid.value && fileType.value === FileType.IMAGE);

const cssClasses = computed(() => ({ _disabled: props.disabled }));

const dirtyId = computed(() => props.dirtyId);
const { setDirty } = useDirty(dirtyId);

const handleFileChange = async (event: FileUploadEntry[]) => {
  clearErrorState();
  fileResponse.value = null;
  const changedFiles = event;
  if (!changedFiles || changedFiles.length === 0) {
    if (fileEntry.value) {
      fileEntry.value.isValid = false;
    }
    return;
  }
  isUploading.value = true;
  fileEntry.value = changedFiles[0];
  emit("fileChanged", fileEntry.value);

  if (fileEntry.value) {
    const isValid = await validateFile(fileEntry.value);

    if (!isValid) {
      fileEntry.value = null;
    } else {
      fileEntry.value.isValid = true;
      try {
        await uploadFile();
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }
  }
  isUploading.value = false;
  setDirty(dirtyId.value);
};

const handleRemove = () => {
  if (!fileEntry.value) return;
  fileEntry.value = null;
  clearErrorState();
  emit("fileChanged", fileEntry.value);
};

const handleCropping = () => {
  if (!fileEntry.value) return;
  cropModalId.value = modal.show(
    CroppingCanvasModal,
    {
      file: fileEntry.value.file,
      croppingConfiguration: props.croppingConfiguration,
      primaryBtnLabel: t("cropping_crop_and_use"),
      secondaryBtnLabel: t("cropping_use_original"),
    },
    {
      cropped: async (event) => {
        emit("update:fileEntry", null);
        if (cropModalId.value) {
          modal.remove(cropModalId.value);
        }
        if (!fileEntry.value) return;
        fileEntry.value.file = event.file;
        if (notNull(event.base64)) {
          // Type assertion for extended File interface with base64 property
          (fileEntry.value.file as File & { base64: string }).base64 = event.base64;
        }
        try {
          await uploadFile();
        } catch (error) {
          console.error("Upload after cropping failed:", error);
        }
      },
    },
    true,
  );
};

// Set error message object
const setErrorMessage = (title: string, message: string | string[] = "") => {
  error.value = { title, message };
};

// Clear all error message object
const clearErrorState = () => {
  error.value = { title: "", message: "" };
};

const showFileUploader = () => {
  fileUploader.value.$refs.fileInput.click();
};

const emitEvent = (data: FileResponse) => {
  fileResponse.value = data;
  if (data.errorTitle) {
    setErrorMessage(data.errorTitle, data.errorMessage || "");
  }
  emit("update:fileEntry", data);
};

const { validateFile, uploadFile, getFileType, getMediaType } = useFileUploader(
  uploadFlow,
  emitEvent,
  fileEntry,
);

watch(
  fileEntry,
  (newFile) => {
    if (newFile) {
      newFile.isValid = newFile.isValid ?? false;
      if (newFile && getFileType(newFile.file) === FileType.IMAGE) {
        if (fileEntry.value?.file) imgSrc.value = URL.createObjectURL(fileEntry.value.file);
      }
    }
  },
  { immediate: true, deep: true },
);

onBeforeMount(() => {
  if (!props.addedFile) {
    return;
  }
  handleFileChange([
    {
      file: props.addedFile,
      invalidMessage: "",
      invalidMessageTitle: "",
      state: "",
      originalFileName: props.addedFile.name,
      isValid: undefined,
      uuid: "",
      compressed: false,
    },
  ]);
});

defineExpose({
  triggerFileUpload: uploadFile,
  showFileUploader,
  setErrorMessage,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.file-uploader {
  .acceptable-files-info {
    @include carbon--type-style("helper-text-01", true);
    color: $text-02;
    margin-bottom: $spacing-06;

    .bx--link {
      @include carbon--type-style("helper-text-01", true);

      // Override generic styling for bx--link in bx--modal having different type token
      .bx--modal & {
        @include carbon--type-style("helper-text-01", true);
      }
    }
  }

  &._disabled {
    &:active {
      pointer-events: none;
    }

    .bx--file {
      .bx--file__drop-container {
        &::before {
          background-image: url("/src/assets/images/placeholder/empty-states/DropzoneDisabled.svg");
        }
      }
    }

    .bx--file-browse-btn {
      text-decoration: none;
      cursor: not-allowed;
    }

    .message-component {
      color: $disabled-02;
    }
  }

  .uploader-wrapper {
    position: relative;
    margin-bottom: $spacing-05;

    &:has(.inline-loader:not(._loading)) {
      margin-bottom: 0;
    }

    .inline-loader._loading {
      border-radius: 10px;

      + .cv-file-uploader {
        .bx--file__drop-container {
          border-color: transparent;

          > * {
            display: none;
          }

          &:before {
            background: none;
          }
        }
      }
    }
  }

  .message-component {
    text-align: center;
  }

  .image-placeholder {
    display: flex;
    background-color: $ui-03;

    img {
      height: 8.75rem;
      margin: 0 auto;
      object-fit: contain;
    }
  }

  .bx--file--label,
  .bx--label-description,
  .bx--file-container {
    display: none;
  }

  .bx--file-browse-btn {
    margin-bottom: 0;
  }

  .attachment-placeholder {
    background-color: $ui-01;
    border-radius: 10px;
    margin-bottom: $spacing-06;

    > .title {
      // To cater for trash and crop button
      max-width: calc(100% - 7rem);
    }

    .message-component {
      text-align: left;

      .content {
        &[data-text*="html5"] {
          text-transform: uppercase;
        }
      }
    }
  }

  .more-than-hundred {
    @include carbon--type-style("helper-text-01", true);
    display: flex;
    margin: -($spacing-05) 0 $spacing-03 0;
    color: $support-01;

    > svg {
      margin-right: $spacing-03;
    }
  }
}
</style>
